import { CHART_GRADES, type DistrictStats, type GradeRow } from './districtStatsApi';

export type ChartView = 'ml' | 'fl';
export type SegmentKind = 'dark' | 'pale' | 'grey';
export type Segment = { kind: SegmentKind; count: number; labeled: boolean };
export type ChartColumn = { grade: string; total: number; segments: Segment[] };
export type LegendEntry = { kind: SegmentKind; text: string };
export type ChartModel = {
  title: string;
  note?: string;
  legend?: LegendEntry[];
  toggle: boolean;
  columns: ChartColumn[];
  max: number;
  // false hides the per-column totals: the placeholder chart has none.
  totals?: boolean;
};

export const BAR_HEIGHT = 150;
export const LABEL_MIN_PX = 24;

const ML_TITLE = 'Active ML students by grade';
const FL_TITLE = 'Students on Flashlight360 by grade';

const sum = (rows: GradeRow[], pick: (row: GradeRow) => number) =>
  rows.reduce((total, row) => total + pick(row), 0);
const mlTotal = (row: GradeRow) => row.mlOnFlashlight + row.mlNotOnFlashlight;
const flTotal = (row: GradeRow) => row.flashlightActiveMl + row.flashlightNotActiveMl;

function columns(rows: GradeRow[], build: (row: GradeRow) => { total: number; segments: Segment[] }): ChartColumn[] {
  return rows.map((row) => {
    const { total, segments } = build(row);
    return { grade: row.grade, total, segments: segments.filter((segment) => segment.count > 0) };
  });
}

export function segmentHeight(count: number, max: number): number {
  return Math.round((BAR_HEIGHT * count) / Math.max(1, max));
}

// TEMPORARY: the shape the coming-soon District Stats card draws while the
// stats resolver is not deployed. Grey bars in the proportions of the Paper
// mockup, no totals and no note; the real chart replaces it once stats arrive.
const PLACEHOLDER_HEIGHTS = [92, 72, 119, 94, 83, 99, 143, 90, 114, 99, 74, 76, 74];

export function placeholderChartModel(): ChartModel {
  return {
    title: FL_TITLE,
    toggle: false,
    totals: false,
    columns: CHART_GRADES.map((grade, i) => {
      const height = PLACEHOLDER_HEIGHTS[i] ?? 0;
      return { grade, total: height, segments: [{ kind: 'grey', count: height, labeled: false }] };
    }),
    max: Math.max(...PLACEHOLDER_HEIGHTS),
  };
}

export function chartModel(stats: DistrictStats, view: ChartView): ChartModel {
  const rows = stats.grades;
  if (!stats.sisConnected) {
    return {
      title: FL_TITLE,
      note: `${sum(rows, flTotal)} students`,
      toggle: false,
      columns: columns(rows, (row) => ({ total: flTotal(row), segments: [{ kind: 'dark', count: flTotal(row), labeled: false }] })),
      max: Math.max(0, ...rows.map(flTotal)),
    };
  }
  if (!stats.flashlightConnected) {
    return {
      title: ML_TITLE,
      note: `${sum(rows, mlTotal)} students`,
      toggle: false,
      columns: columns(rows, (row) => ({ total: mlTotal(row), segments: [{ kind: 'dark', count: mlTotal(row), labeled: false }] })),
      max: Math.max(0, ...rows.map(mlTotal)),
    };
  }
  const other = stats.flashlightStudentsNotActiveMl ?? sum(rows, (row) => row.flashlightNotActiveMl);
  const toggle = other > 0;
  const max = Math.max(0, ...rows.map((row) => Math.max(mlTotal(row), flTotal(row))));
  if (!toggle || view === 'ml') {
    const on = sum(rows, (row) => row.mlOnFlashlight);
    const off = sum(rows, (row) => row.mlNotOnFlashlight);
    return {
      title: ML_TITLE,
      note: off > 0 ? undefined : `${on} students`,
      legend:
        off > 0
          ? [
              { kind: 'dark', text: `On Flashlight360 · ${on}` },
              { kind: 'pale', text: `Not on Flashlight360 · ${off}` },
            ]
          : undefined,
      toggle,
      columns: columns(rows, (row) => ({
        total: mlTotal(row),
        segments: [
          { kind: 'pale', count: row.mlNotOnFlashlight, labeled: false },
          { kind: 'dark', count: row.mlOnFlashlight, labeled: off > 0 },
        ],
      })),
      max,
    };
  }
  return {
    title: FL_TITLE,
    legend: [
      { kind: 'dark', text: `Active ML · ${sum(rows, (row) => row.flashlightActiveMl)}` },
      { kind: 'grey', text: `Not an active ML · ${other}` },
    ],
    toggle,
    columns: columns(rows, (row) => ({
      total: flTotal(row),
      segments: [
        { kind: 'grey', count: row.flashlightNotActiveMl, labeled: false },
        { kind: 'dark', count: row.flashlightActiveMl, labeled: true },
      ],
    })),
    max,
  };
}
